import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, Injectable } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { catchError, filter, map, Observable, of, startWith, switchMap, timeout } from 'rxjs';
import { ListingStatusEnum } from 'shared/constants/listing-statuses';
import { ListingPartPhase, PartStatusInfo } from 'shared/interfaces/listing-parts.interface';
import { ContextAwareSidebarService } from 'shared/services/context-aware-sidebar.service';
import { EventBusService } from 'shared/services/event-bus.service';
import { PermissionsService } from 'shared/services/permissions.service';
import {
    IceVisualListingStatusApiResponse,
    ListingVisualsStatusChangedData,
    VisualsPartState,
    VisualsPartStatus,
    VisualsStatusEnum,
} from 'visuals/interfaces/ice-visual-listing.interface';

const VISUALS_API_URL = environment.BASE_URL + 'visualManagementApi';
const VISUALS_STATUS_TIMEOUT_MS = 10000;
const NO_VISUALS_PART_STATUS_CODE = 412;

const CONTENT_STATUS_INFO: { [key in ListingStatusEnum]: PartStatusInfo } = {
    [ListingStatusEnum.LIVE]: { label: 'listings.statuses.LIVE', tone: 'live' },
    [ListingStatusEnum.DRAFT]: { label: 'listings.statuses.DRAFT', tone: 'draft' },
    [ListingStatusEnum.FAILURES]: { label: 'listings.statuses.DRAFT', tone: 'draft' },
    [ListingStatusEnum.REJECTED]: { label: 'listings.statuses.DRAFT_REJECTED', tone: 'draft' },
    [ListingStatusEnum.REJECTED_WITH_COMMENTS]: { label: 'listings.statuses.DRAFT_REJECTED', tone: 'draft' },
    [ListingStatusEnum.APPROVAL]: { label: 'listings.statuses.DRAFT_WAITING_APPROVAL', tone: 'pending' },
    [ListingStatusEnum.PUBLISHING_IN_PROGRESS]: { label: 'listings.statuses.DRAFT_PROCESSING', tone: 'pending' },
    [ListingStatusEnum.BULK_PROCESSING]: { label: 'listings.statuses.BULK_PROCESSING', tone: 'pending' },
    [ListingStatusEnum.NEW]: { label: 'listings.statuses.NEW', tone: 'neutral' },
    [ListingStatusEnum.UNKNOWN]: { label: 'generalKeywords.unknown', tone: 'neutral' },
};

const VISUALS_STATUS_INFO: { [key in Exclude<VisualsPartStatus, VisualsPartState.HIDDEN>]: PartStatusInfo } = {
    [VisualsStatusEnum.LIVE]: { label: 'listings.statuses.LIVE', tone: 'live' },
    [VisualsStatusEnum.DRAFT]: { label: 'listings.statuses.DRAFT', tone: 'draft' },
    [VisualsStatusEnum.PENDING_ACTIVATION]: { label: 'listing.partStatus.pendingActivation', tone: 'pending' },
    [VisualsPartState.UNAVAILABLE]: { label: 'listing.partStatus.unavailable', tone: 'neutral' },
    [VisualsPartState.LOADING]: { label: 'listing.partStatus.loading', tone: 'neutral' },
};

const VISUALS_PHASES: { [key in VisualsPartStatus]: ListingPartPhase } = {
    [VisualsStatusEnum.LIVE]: ListingPartPhase.LIVE,
    [VisualsStatusEnum.DRAFT]: ListingPartPhase.DRAFT,
    [VisualsStatusEnum.PENDING_ACTIVATION]: ListingPartPhase.PENDING,
    [VisualsPartState.UNAVAILABLE]: ListingPartPhase.UNAVAILABLE,
    [VisualsPartState.LOADING]: ListingPartPhase.LOADING,
    [VisualsPartState.HIDDEN]: ListingPartPhase.HIDDEN,
};

@Injectable({
    providedIn: 'root',
})
export class ListingPartsStatusService {
    contentStatus = computed<ListingStatusEnum | null>(() => this.contextAwareSidebarService.listing()?.listingStatus ?? null);

    visualsStatus = toSignal(
        toObservable(this.contextAwareSidebarService.listing).pipe(
            switchMap((listing) => {
                if (!listing) {
                    return of(VisualsPartState.LOADING);
                }
                if (!listing.hasVisualsAccess) {
                    return of(VisualsPartState.HIDDEN);
                }
                return this.getVisualsStatus(listing.id).pipe(startWith(VisualsPartState.LOADING));
            }),
        ),
        { initialValue: VisualsPartState.LOADING },
    );

    contentStatusInfo = computed(() => {
        const contentStatus = this.contentStatus();
        return contentStatus ? CONTENT_STATUS_INFO[contentStatus] : null;
    });

    visualsStatusInfo = computed(() => {
        const visualsStatus = this.visualsStatus();
        return visualsStatus === VisualsPartState.HIDDEN ? null : VISUALS_STATUS_INFO[visualsStatus];
    });

    contentPhase = computed(() => {
        switch (this.contentStatus()) {
            case null:
                return ListingPartPhase.LOADING;
            case ListingStatusEnum.LIVE:
                return ListingPartPhase.LIVE;
            case ListingStatusEnum.APPROVAL:
                return ListingPartPhase.PENDING;
            case ListingStatusEnum.PUBLISHING_IN_PROGRESS:
                return ListingPartPhase.LOCKED;
            default:
                return ListingPartPhase.DRAFT;
        }
    });

    visualsPhase = computed(() => VISUALS_PHASES[this.visualsStatus()]);

    allPartsLive = computed(
        () =>
            this.contentPhase() === ListingPartPhase.LIVE &&
            ![ListingPartPhase.DRAFT, ListingPartPhase.PENDING].includes(this.visualsPhase()),
    );

    private isApprover = toSignal(this.permissionsService.userHasAccess('listings.saveOptions.submitForPublishing'), {
        initialValue: false,
    });

    isVisualsReadOnly = computed(() => {
        if (this.contextAwareSidebarService.listing()?.isReadOnly) {
            return true;
        }
        switch (this.visualsPhase()) {
            case ListingPartPhase.DRAFT:
                return false;
            case ListingPartPhase.PENDING:
                return !this.isApprover();
            default:
                return true;
        }
    });

    constructor(
        private http: HttpClient,
        private contextAwareSidebarService: ContextAwareSidebarService,
        private eventBusService: EventBusService,
        private permissionsService: PermissionsService,
    ) {
        this.reloadOnVisualsStatusChange();
    }

    private reloadOnVisualsStatusChange() {
        this.eventBusService
            .getObservableForEventStream('listing:visualsStatusUpdate')
            .pipe(
                map((data) => (JSON.parse(data as string) as ListingVisualsStatusChangedData).ListingId),
                filter((listingId) => listingId === this.contextAwareSidebarService.listing()?.id),
                switchMap((listingId) => {
                    const loadedStatus = this.visualsStatus();
                    if (loadedStatus === VisualsPartState.LOADING || loadedStatus === VisualsPartState.HIDDEN) {
                        return of(false);
                    }
                    return this.getVisualsStatus(listingId).pipe(map((status) => this.isVisualsStatus(status) && status !== loadedStatus));
                }),
                filter(Boolean),
                takeUntilDestroyed(),
            )
            .subscribe(() => window.location.reload());
    }

    private isVisualsStatus(status: VisualsPartStatus): boolean {
        return Object.values(VisualsStatusEnum).includes(status as VisualsStatusEnum);
    }

    private getVisualsStatus(listingId: string): Observable<VisualsPartStatus> {
        return this.http
            .get<IceVisualListingStatusApiResponse>(`${VISUALS_API_URL}/listings/get-listing-status-data?listingId=${listingId}`)
            .pipe(
                timeout(VISUALS_STATUS_TIMEOUT_MS),
                map((response) => {
                    const status = response?.status;
                    return status && status !== VisualsStatusEnum.UNKNOWN ? status : VisualsPartState.UNAVAILABLE;
                }),
                catchError((error: HttpErrorResponse) =>
                    of(error.status === NO_VISUALS_PART_STATUS_CODE ? VisualsPartState.HIDDEN : VisualsPartState.UNAVAILABLE),
                ),
            );
    }
}
